<template>
  <div class="cost-report">
    <div class="cost-header">
      <h3>Processing Costs & Cache Performance</h3>
      <div class="total-cost">
        <span class="cost-amount">{{ formatCurrency(costs.cost) }}</span>
        <span class="cache-savings">
          {{ formatPercentage(calculateCachePercentage(costs.inputTokens, costs.cachedTokens)) }} cached
        </span>
      </div>
    </div>

    <div class="job-summary">
      <div class="metric">
        <span class="label">Input Tokens:</span>
        <span class="value">{{ formatTokens(costs.inputTokens) }}</span>
      </div>
      <div class="metric">
        <span class="label">Output Tokens:</span>
        <span class="value">{{ formatTokens(costs.outputTokens) }}</span>
      </div>
      <div class="metric">
        <span class="label">Cached Tokens:</span>
        <span class="value">{{ formatTokens(costs.cachedTokens) }}</span>
      </div>
      <div class="metric">
        <span class="label">Cache Efficiency:</span>
        <span class="value cache-percentage">
          {{ formatPercentage(calculateCachePercentage(costs.inputTokens, costs.cachedTokens)) }}
        </span>
      </div>
      <div v-if="costs.model" class="metric">
        <span class="label">Model:</span>
        <span class="value">{{ formatModelName(costs.model) }}</span>
      </div>
      <div v-if="costs.durationMs" class="metric">
        <span class="label">Processing Time:</span>
        <span class="value">{{ formatDuration(costs.durationMs) }}</span>
      </div>
    </div>

    <div class="frameworks">
      <h4>Framework Breakdown</h4>
      <div v-for="framework in costs.frameworks" :key="framework.frameworkId" class="framework">
        <div class="framework-header">
          <span class="framework-name">{{ framework.frameworkId }}</span>
          <div class="framework-stats">
            <span class="cost">{{ formatCurrency(framework.cost) }}</span>
            <span class="cache-stat">
              {{ formatPercentage(calculateCachePercentage(framework.inputTokens, framework.cachedTokens)) }} cached
            </span>
          </div>
        </div>
        <div class="framework-details">
          <div class="detail">
            <span class="detail-label">Input tokens:</span>
            <span class="detail-value">{{ formatTokens(framework.inputTokens) }}</span>
          </div>
          <div class="detail">
            <span class="detail-label">Output tokens:</span>
            <span class="detail-value">{{ formatTokens(framework.outputTokens) }}</span>
          </div>
          <div class="detail">
            <span class="detail-label">Cached tokens:</span>
            <span class="detail-value">{{ formatTokens(framework.cachedTokens) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { JobCosts } from '../types';

interface Props {
  costs: JobCosts;
}

defineProps<Props>();

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(amount);
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  } else if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}K`;
  }
  return tokens.toString();
}

function calculateCachePercentage(inputTokens: number, cachedTokens: number): number {
  return inputTokens > 0 ? (cachedTokens / inputTokens) * 100 : 0;
}

function formatPercentage(percentage: number): string {
  return `${percentage.toFixed(1)}%`;
}

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Fallback to manual formatting since Intl.DurationFormat may not be available
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

function formatModelName(model: string): string {
  const modelPart = model.split('/')[1] || model;
  return modelPart
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
</script>

<style scoped>
.cost-report {
  padding: 1rem;
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 8px;
  background: var(--background-color, #fff);
}

.cost-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color, #e0e0e0);
}

.cost-header h3 {
  margin: 0;
  color: var(--text-color, #333);
}

.total-cost {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.cost-amount {
  font-size: 1.25rem;
  font-weight: bold;
  color: var(--color-accent);
}

.cache-savings {
  font-size: 0.85rem;
  color: var(--color-success);
  background: rgba(26, 127, 55, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.job-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  background: var(--secondary-background, #f8f9fa);
  border-radius: 4px;
}

.label {
  color: var(--text-secondary, #666);
  font-size: 0.9rem;
}

.value {
  font-weight: 500;
  color: var(--text-color, #333);
}

.cache-percentage {
  color: var(--color-success);
  font-weight: bold;
}

.frameworks h4 {
  margin: 0 0 1rem 0;
  color: var(--text-color, #333);
}

.framework {
  margin-bottom: 1rem;
  border: 1px solid var(--border-light, #f0f0f0);
  border-radius: 6px;
  overflow: hidden;
}

.framework-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--secondary-background, #f8f9fa);
  border-bottom: 1px solid var(--border-light, #f0f0f0);
}

.framework-name {
  font-weight: 500;
  color: var(--text-color, #333);
}

.framework-stats {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.cost {
  font-weight: bold;
  color: var(--color-accent);
}

.cache-stat {
  font-size: 0.85rem;
  color: var(--color-success);
  background: rgba(26, 127, 55, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.framework-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  padding: 0.75rem;
}

.detail {
  display: flex;
  justify-content: space-between;
}

.detail-label {
  color: var(--text-secondary, #666);
  font-size: 0.85rem;
}

.detail-value {
  font-weight: 500;
  color: var(--text-color, #333);
  font-size: 0.85rem;
}
</style>
